import type { RoomAudienceItemV2, roomPersonInfoListJson } from '@/views/Voice/voiceMain/myRoom/userListV2/service';
import type {
  AudienceV2ChangeItem,
  AudienceV2LocalItem,
  AudienceV2RoomPayload,
  AudienceV2RoomState,
  AudienceV2State
} from './audienceV2';

export function getAudienceV2RoomKey(payload: AudienceV2RoomPayload) {
  return `${payload.fromType}:${payload.roomId}`;
}

export function createAudienceV2RoomState(payload: AudienceV2RoomPayload): AudienceV2RoomState {
  return {
    roomId: Number(payload.roomId),
    fromType: payload.fromType,
    loading: false,
    hasMore: true,
    nextOffset: null,
    loaded: false,
    sequence: 0,
    needRefresh: false,
    refreshReason: undefined,
    items: []
  };
}

export function getRoomState(state: AudienceV2State, payload: AudienceV2RoomPayload) {
  const roomKey = getAudienceV2RoomKey(payload);
  return state.rooms[roomKey] || createAudienceV2RoomState(payload);
}

export function setRoomState(state: AudienceV2State, room: AudienceV2RoomState): AudienceV2State {
  return {
    ...state,
    rooms: {
      ...state.rooms,
      [getAudienceV2RoomKey(room)]: room
    }
  };
}

function toFiniteNumber(value: any, fallback?: number) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function toOnlineBoolean(value: any, fallback = true) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value !== 'false';
  return fallback;
}

function normalizeUserInfo(userId: number, userInfo?: roomPersonInfoListJson) {
  return {
    ...(userInfo || {}),
    userId: toFiniteNumber(userInfo?.userId, userId) as number
  } as roomPersonInfoListJson;
}

export function normalizeAudienceV2Item(item: RoomAudienceItemV2 | AudienceV2LocalItem) {
  const userId = toFiniteNumber((item as any)?.userId ?? (item as any)?.userInfo?.userId);
  if (userId === undefined) return null;
  const index = toFiniteNumber((item as any)?.index, 0) as number;
  const onlineDeviceTypes = Array.isArray((item as any)?.onlineDeviceTypes)
    ? (item as any).onlineDeviceTypes
    : undefined;

  return {
    userId,
    index,
    online: toOnlineBoolean((item as any)?.online, true),
    onlineDeviceTypes,
    userInfo: normalizeUserInfo(userId, (item as any)?.userInfo)
  };
}

export function normalizeAudienceV2Items(items: Array<RoomAudienceItemV2 | AudienceV2LocalItem>) {
  return (items || []).reduce<AudienceV2LocalItem[]>((result, item) => {
    const normalized = normalizeAudienceV2Item(item);
    if (normalized) result.push(normalized);
    return result;
  }, []);
}

function mergeAudienceV2Item(prev: AudienceV2LocalItem | undefined, next: AudienceV2LocalItem): AudienceV2LocalItem {
  if (!prev) return next;
  return {
    ...prev,
    ...next,
    onlineDeviceTypes: next.onlineDeviceTypes || prev.onlineDeviceTypes,
    userInfo: {
      ...prev.userInfo,
      ...next.userInfo,
      userId: next.userInfo?.userId ?? next.userId
    } as roomPersonInfoListJson
  };
}

export function mergeAndSortAudienceV2Items(items: AudienceV2LocalItem[]) {
  const itemMap = new Map<number, { item: AudienceV2LocalItem; order: number }>();
  items.forEach((item, order) => {
    const prev = itemMap.get(item.userId);
    itemMap.set(item.userId, {
      item: mergeAudienceV2Item(prev?.item, item),
      order: prev?.order ?? order
    });
  });

  return Array.from(itemMap.values())
    .sort((a, b) => a.item.index - b.item.index || a.order - b.order)
    .map(item => item.item);
}

function normalizeChangeItem(change: AudienceV2ChangeItem, prev?: AudienceV2LocalItem) {
  const userId = toFiniteNumber(change.userId);
  if (userId === undefined) return null;
  const index = toFiniteNumber(change.index, prev?.index);
  if (index === undefined) return null;
  return {
    userId,
    index,
    online: toOnlineBoolean(change.online, true),
    onlineDeviceTypes: Array.isArray(change.onlineDeviceTypes) ? change.onlineDeviceTypes : prev?.onlineDeviceTypes,
    userInfo: normalizeUserInfo(userId, change.userInfo || prev?.userInfo)
  };
}

/** ENTER 后从其他已加载房间的 audience 本地态移除同一用户，避免多房间重复展示。 */
export function removeUserFromOtherAudienceRooms(
  state: AudienceV2State,
  enterPayload: AudienceV2RoomPayload,
  userId: number
) {
  const enterRoomKey = getAudienceV2RoomKey(enterPayload);
  let nextState = state;

  Object.keys(state.rooms).forEach(roomKey => {
    if (roomKey === enterRoomKey) return;

    const room = state.rooms[roomKey];
    if (!room?.loaded || !room.items.some(item => item.userId === userId)) return;

    nextState = setRoomState(nextState, {
      ...room,
      items: room.items.filter(item => item.userId !== userId)
    });
  });

  return nextState;
}

export function applyAudienceV2Changes(room: AudienceV2RoomState, changes: AudienceV2ChangeItem[]) {
  let items = room.items;
  (changes || []).forEach(change => {
    const userId = toFiniteNumber(change.userId);
    if (userId === undefined) return;
    const prev = items.find(item => item.userId === userId);

    if (change.eventType === 'ENTER') {
      const nextItem = normalizeChangeItem(change, prev);
      if (!nextItem) return;
      items = mergeAndSortAudienceV2Items([...items, nextItem]);
      return;
    }

    if (change.eventType === 'OFFLINE') {
      if (!prev) return;
      const nextOnline = change.online !== undefined ? change.online : false;
      // OFFLINE 表示连接态变更：online=false 离线，online=true 恢复在线。
      items = items.map(item =>
        item.userId === userId
          ? mergeAudienceV2Item(item, {
              ...item,
              online: nextOnline,
              userInfo: normalizeUserInfo(userId, change.userInfo || item.userInfo)
            })
          : item
      );
      return;
    }

    if (change.eventType === 'UPDATE') {
      if (!prev) return;
      const nextItem = normalizeChangeItem(change, prev);
      if (!nextItem) return;
      items = items.map(item => (item.userId === userId ? mergeAudienceV2Item(item, nextItem) : item));
      return;
    }

    if (change.eventType === 'REMOVE') {
      items = items.filter(item => item.userId !== userId);
    }
  });
  return mergeAndSortAudienceV2Items(items);
}
